<script lang="ts">
    import { createEventDispatcher, tick } from 'svelte';

    export let showModal = false;
    export let modalWidth = 300;
    export let modalClass = '';
    export let closeOnEscape = true;
    export let closeOnBackdrop = false;
    export let labelledBy = '';

    const dispatch = createEventDispatcher();
    let dialogEl: HTMLDivElement | null = null;
    let previouslyFocused: Element | null = null;

    const FOCUSABLE =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function requestClose(): void {
        dispatch('close');
    }

    function handleKeydown(event: KeyboardEvent): void {
        if (!showModal) return;
        if (event.key === 'Escape' && closeOnEscape) {
            event.preventDefault();
            requestClose();
            return;
        }
        if (event.key === 'Tab' && dialogEl) {
            const focusable = dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    }

    function handleBackdropClick(): void {
        if (closeOnBackdrop) requestClose();
    }

    $: if (typeof document !== 'undefined') {
        if (showModal) {
            previouslyFocused = document.activeElement;
            document.body.style.overflow = 'hidden';
            tick().then(() => {
                if (!dialogEl) return;
                const autofocus = dialogEl.querySelector<HTMLElement>('[data-autofocus]');
                const first = autofocus || dialogEl.querySelector<HTMLElement>(FOCUSABLE);
                if (first) first.focus();
                else dialogEl.focus();
            });
        } else {
            document.body.style.overflow = '';
            if (previouslyFocused && previouslyFocused instanceof HTMLElement) {
                previouslyFocused.focus();
                previouslyFocused = null;
            }
        }
    }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if showModal}
<div
    class="shade"
    role="presentation"
    on:click|self={handleBackdropClick}
    on:keydown|self={(e) => { if (e.key === 'Enter' || e.key === ' ') handleBackdropClick(); }}
>
    <div
        class="ui modal {modalClass}"
        style="width:{modalWidth}px; min-width:{modalWidth}px;"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || undefined}
        tabindex="-1"
        bind:this={dialogEl}
    >
        <div class="header">
            <slot name="header">
            <!-- Default content goes here -->
            </slot>
        </div>
        <div class="content">
            <slot name="body">
            <!-- Default content goes here -->
            </slot>
        </div>
        <div class="actions">
            <slot name="footer">
            <!-- Default content goes here -->
            </slot>
        </div>
    </div>
</div>
{/if}

<style>
    .modal {
        max-height: 80%;
        display: flex;
        flex-direction: column;
        overscroll-behavior: contain;
    }

    .modal .header,
    .modal .content,
    .modal .actions {
        width: 100%;
        box-sizing: border-box;
        flex-shrink: 0;
    }

    .modal .content {
        overflow-y: auto;
        flex: 1 1 auto;
        min-height: 0;
    }

    .ui.modal:focus {
        outline: none;
    }

    .ui.modal:focus-visible {
        box-shadow: var(--focus-ring);
    }
</style>
